"use client";

import Script from "next/script";
import { useEffect, useRef, useState } from "react";
import { usePathname } from "next/navigation";

const CARGURUS_ACCESS_KEY = "00000000-0000-1bda-0000-00000006e01a";
const EXCLUDED_PATHS = new Set(["/faqs"]);

/**
 * CarGurus Partner Metrics tracking.
 * The retired CloudFront Snowplow distribution has no DNS records; use the
 * independent vendor-hosted metrics client and its noscript fallback.
 * By placing this code on your website, you authorize CarGurus to access
 * and use website visitor data to optimize digital advertising performance.
 */
export default function CarGurusTracking() {
    const pathname = usePathname();
    const [partnerConfigured, setPartnerConfigured] = useState(false);
    const lastTrackedPath = useRef<string | null>(null);

    useEffect(() => {
        if (EXCLUDED_PATHS.has(pathname)) return;
        // The vendor reads all three namespaces immediately when its script executes.
        window.CarGurus = window.CarGurus || {};
        window.CarGurus.Partner = window.CarGurus.Partner || {};
        window.CarGurus.Partner.Metrics = window.CarGurus.Partner.Metrics || {};
        window.CarGurus.Partner.Metrics.Options = window.CarGurus.Partner.Metrics.Options || {};
        window.CarGurus.Partner.Metrics.Options.AccessKey = CARGURUS_ACCESS_KEY;
        window.CarGurus.Partner.Metrics.Options.Debug = false;
        setPartnerConfigured(true);
    }, [pathname]);

    useEffect(() => {
        const metrics = window.CarGurus?.Partner?.Metrics?.Code;
        if (EXCLUDED_PATHS.has(pathname) || !metrics || lastTrackedPath.current === pathname) return;
        metrics.view();
        lastTrackedPath.current = pathname;
    }, [pathname]);

    if (EXCLUDED_PATHS.has(pathname)) {
        return null;
    }

    return (
        <>
            {partnerConfigured && <Script
                id="cargurus-partner-metrics"
                src="https://static.cargurus.com/js/api/en_US/2.0/partnermetrics.js"
                strategy="afterInteractive"
                onReady={() => {
                    // The vendor client records its initial page during initialization.
                    // Subsequent App Router navigations need a virtual page view.
                    lastTrackedPath.current = pathname;
                }}
            />}
            <noscript>
                <div style={{ display: "inline" }}>
                    {/* Vendor noscript beacon must use the direct endpoint. */}
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img
                        height={1}
                        width={1}
                        style={{ borderStyle: "none" }}
                        alt=""
                        src={`https://www.cargurus.com/Cars/api/2.0/recordMetricsImage.action?k=${CARGURUS_ACCESS_KEY}&x=%7B%22ns%22%3A1%7D`}
                    />
                </div>
            </noscript>
        </>
    );
}
